import {
  OrchestrationWorkflowDocument,
  OrchestrationWorkflowFragment,
  WorkflowRunStatus,
  useSetWorkflowActiveMutation,
  useStartWorkflowNowMutation,
} from "apollo/types";
import { Button, SecondaryButton } from "components/elements/Button";
import { LoadingFull } from "components/elements/LoadingComponents";
import MainPageLayout from "components/layouts/MainPageLayout";
import { forwardRef, useState } from "react";
import { LocationGenerics } from "routes";

import confirm from "@/components/elements/Confirm";
import { DetailItem } from "@/components/elements/DataBox";
import { FormatRelativeTime } from "@/components/elements/FormatRelativeTime";
import Toggle from "@/components/elements/Toggle";
import Tooltip from "@/components/elements/Tooltip";
import FieldLabel from "@/components/primitives/InputLabel";
import classNames from "@/helpers/classNames";
import { useDisplayCron } from "@/hooks/useCron";
import { useToast } from "@/providers/ToastProvider";
import { formatDate } from "@/shared/formatters";
import { PlayIcon } from "@heroicons/react/24/outline";
import { useMatch } from "@tanstack/react-location";
import { ComponentProps } from "@tw-classed/react";

import { OrchestrationTabsLayout } from "./OrchestrationTabsLayout";
import { WorkflowRun } from "./components/WorkflowRun";
import { useOrchestrationWorkflow } from "./components/useWorkflow";

export default function OrchestrationWorkflowPage() {
  const match = useMatch<LocationGenerics>();

  const { workflow, nextRunStartingNow } = useOrchestrationWorkflow(
    match.params.workflowId,
  );

  if (!workflow) {
    return (
      <MainPageLayout>
        <LoadingFull />
      </MainPageLayout>
    );
  }
  return (
    <OrchestrationTabsLayout
      current="orchestration"
      action={<ToggleActiveButton workflow={workflow} />}
    >
      <OrchestrationWorkflow
        workflow={workflow}
        nextRunStartingNow={nextRunStartingNow}
      />
    </OrchestrationTabsLayout>
  );
}

function ToggleActiveButton({
  workflow,
}: ComponentProps<typeof Button> & {
  workflow: OrchestrationWorkflowFragment;
}) {
  const [checked, setChecked] = useState(workflow.active);
  const toast = useToast();
  const [setWorkflowActive, { loading }] = useSetWorkflowActiveMutation();

  return (
    <div className="flex items-center gap-4">
      <FieldLabel
        className="mb-0 text-sm font-medium"
        htmlFor="toggle-orchestration-active"
      >
        {workflow.active ? "Orchestration Enabled" : "Orchestration Disabled"}
      </FieldLabel>
      <Toggle
        id="toggle-orchestration-active"
        checked={checked}
        onChange={async (nextChecked) => {
          if (
            nextChecked === false &&
            !(await confirm({
              title: "Are you sure?",
              message:
                "Are you sure you want to disable this orchestration? All future runs will be skipped until re-enabled.",
              confirmButtonText: "Disable",
              type: "danger",
            }))
          ) {
            return; // user cancelled
          }
          const prevChecked = checked;
          setChecked(nextChecked);
          try {
            await setWorkflowActive({
              variables: { workflowId: workflow.id, active: nextChecked },
            });
          } catch (error: any) {
            setChecked(prevChecked); // revert UI
            toast(
              "Failed to update orchestration state",
              error?.message || "An error occurred",
              "error",
            );
          }
        }}
        disabled={loading}
      />
    </div>
  );
}

const OrchestrationWorkflow = (props: {
  workflow: OrchestrationWorkflowFragment;
  nextRunStartingNow: boolean;
}) => {
  const { workflow, nextRunStartingNow } = props;

  const { cronDisplay, cronDisplayVerbose, cronValid } = useDisplayCron(
    workflow.cronExpression,
  );
  return (
    <>
      <div className="mb-8 flex flex-col gap-4">
        <DetailItem title="Name">{workflow.name}</DetailItem>
        {workflow.description && (
          <DetailItem title="Description" className="items-start">
            <p className="whitespace-pre-line leading-tight text-muted-foreground">
              {workflow.description}
            </p>
          </DetailItem>
        )}
        <DetailItem title="Schedule">
          <Tooltip content={cronDisplayVerbose}>
            <span
              className={classNames(!cronValid && "text-red-600", "text-sm")}
            >
              {cronDisplay}
            </span>
          </Tooltip>
        </DetailItem>
        <DetailItem title="Next run">
          {workflow.active ? (
            <div className="flex items-center gap-8">
              {workflow.nextRun?.dueAt ? (
                <Tooltip
                  content={formatDate(workflow.nextRun.dueAt, {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                >
                  <FormatRelativeTime
                    date={workflow.nextRun.dueAt}
                    options={{ style: "long" }}
                    className="text-muted-foreground"
                  />
                </Tooltip>
              ) : workflow.currentRun?.status === WorkflowRunStatus.Running ? (
                <span className="italic text-muted-foreground">Running</span>
              ) : (
                <span className="italic text-muted-foreground">N/A</span>
              )}
              {workflow.currentRun?.status !== WorkflowRunStatus.Running &&
                !nextRunStartingNow && (
                  <Tooltip content="Run orchestration now">
                    <RunNowActionButton
                      workflowId={workflow.id}
                      nextRunStartingNow={nextRunStartingNow}
                      workflowStatus={
                        workflow.currentRun?.status ??
                        WorkflowRunStatus.NotStarted
                      }
                      variant="link"
                      size="sm"
                      icon={<PlayIcon />}
                    />
                  </Tooltip>
                )}
            </div>
          ) : (
            <Tooltip content="Orchestration is currently disabled. Scheduled runs will be skipped until re-enabled.">
              <em className="text-muted-foreground">Orchestration disabled</em>
            </Tooltip>
          )}
        </DetailItem>
      </div>

      <WorkflowRun
        active={workflow.active}
        workflowId={workflow.id}
        isCurrentWorkflow
        nextRunStartingNow={nextRunStartingNow}
        currentRun={workflow.currentRun}
        nextRun={workflow.nextRun}
        cronExpression={workflow.cronExpression}
      />
    </>
  );
};

const RunNowActionButton = forwardRef(
  (
    props: {
      workflowId: string;
      workflowStatus: WorkflowRunStatus;
      nextRunStartingNow: boolean;
    } & ComponentProps<typeof Button>,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    const { workflowId, workflowStatus, nextRunStartingNow, ...buttonProps } =
      props;
    const toast = useToast();

    const [runWorkflow, { loading }] = useStartWorkflowNowMutation({
      refetchQueries: [
        {
          query: OrchestrationWorkflowDocument,
          variables: { workflowId: workflowId },
        },
      ],
      onCompleted: () => {
        toast(
          "Orchestration starting",
          "Orchestration will start now",
          "success",
        );
      },
      onError: (error) => {
        toast("Orchestration failed to start", error.message, "error");
      },
    });

    const running = workflowStatus === WorkflowRunStatus.Running;

    return (
      <SecondaryButton
        ref={ref}
        {...buttonProps}
        disabled={loading || running || props.nextRunStartingNow}
        onClick={() => {
          if (loading || props.nextRunStartingNow) return;
          runWorkflow({ variables: { workflowId: props.workflowId } });
        }}
      >
        Run now
      </SecondaryButton>
    );
  },
);
